import { Link, useParams, Navigate } from "react-router-dom";
import "../../App.css";
import categories from "../../data/categories";
import Footer from "../Footer";
import Reveal from "../Reveal";
import Seo from "../Seo";
import TextCard from "../TextCard";
import "./ProductenDetail.css";

function ProductenDetail() {
	const { slug } = useParams();
	const category = categories.find((c) => c.slug === slug);

	if (!category) {
		return <Navigate to="/producten" replace />;
	}

	// Lowercase the title for mid-sentence use, but keep acronyms ("LED") intact.
	const ctaName = category.title
		.split(" ")
		.map((w) => (/[A-Z]/.test(w) && w === w.toUpperCase() ? w : w.toLowerCase()))
		.join(" ");

	const renderText = (text) =>
		text.split("\n").map((line, idx) => (
			<span key={idx} className="pd-textline">
				{line}
			</span>
		));

	// First sentence of the intro doubles as the meta description; pages
	// without an intro fall back to the first feature's text, then the tease.
	const descSource =
		category.intro || category.features?.[0]?.text || category.tease || "";
	const seoDescription = descSource.split(". ")[0] + ".";

	return (
		<>
			<Seo
				title={`${category.title} huren`}
				description={seoDescription}
				path={`/producten/${category.slug}`}
			/>
			<section
				className="pd-hero"
				style={{
					// Desktop/mobile photos drive the background via CSS vars so a
					// media query can swap them; categories without a mobile photo
					// fall back to the desktop one.
					"--pd-hero-desktop": `url(${category.hero.photo})`,
					"--pd-hero-mobile": `url(${
						category.hero.photoMobile || category.hero.photo
					})`,
					backgroundPosition: category.hero.position,
				}}>
				<div className="pd-hero-overlay" aria-hidden="true"></div>
				<div className="pd-hero-content">
					<span className="pd-hero-eyebrow">DE COLLECTIE</span>
					<h1 className="pd-hero-title">{category.title}</h1>
				</div>
			</section>

			{category.intro && (
				<Reveal as="section" className="pd-intro">
					<p className="pd-intro-text">{category.intro}</p>
				</Reveal>
			)}

			{category.features?.map((f, i) => (
				<Reveal key={`feature-${i}`}>
					<TextCard
						imageUrl={f.image}
						title={f.eyebrow}
						subtitle={f.title}
						text={f.text}
						pullQuote={f.pullQuote}
						reverse={f.reverse}
					/>
				</Reveal>
			))}

			{category.sections?.map((section, i) => (
				<Reveal key={i} as="section" className="pd-section">
					<div className="pd-section-header">
						{section.heading && (
							<h2 className="pd-section-heading">{section.heading}</h2>
						)}
						<p className="pd-section-text">{renderText(section.text)}</p>
						{section.fromPrice && (
							<p className="pd-section-price">
								Vanaf{" "}
								<span className="pd-section-price-amount">
									{section.fromPrice}
								</span>
								{section.priceUnit && (
									<span className="pd-section-price-unit">
										{" "}
										{section.priceUnit}
									</span>
								)}
							</p>
						)}
					</div>

					{section.images.length > 0 && (
						<div className="pd-gallery">
							{section.images.map((img, idx) => (
								<div
									key={idx}
									className="pd-gallery-tile stagger-clip"
									style={{ "--stagger-index": idx }}>
									<img src={img.src} alt={img.alt} loading="lazy" />
								</div>
							))}
						</div>
					)}
				</Reveal>
			))}

			{category.notice && (
				<Reveal as="section" className="pd-notice">
					<div className="pd-notice-inner">
						<span className="pd-notice-label">Let op</span>
						<p className="pd-notice-text">{category.notice}</p>
					</div>
				</Reveal>
			)}

			<Reveal as="section" className="pd-pricing-link">
				<Link to="/prijslijst" className="pd-pricing-anchor">
					Bekijk volledige prijslijst{" "}
					<span className="pd-pricing-arrow">→</span>
				</Link>
			</Reveal>

			<Reveal as="section" className="pd-cta">
				<div className="pd-cta-inner">
					<span className="pd-cta-eyebrow">AANVRAAG</span>
					<h2 className="pd-cta-title">Uw feest begint hier</h2>
					<p className="pd-cta-text">
						Voeg {ctaName} toe aan uw aanvraag — wij nemen
						binnen 24 uur contact met u op.
					</p>
					<Link
						to={`/aanvraag?product=${category.slug}`}
						className="btn btn--primary btn--on-dark btn--lg">
						Toevoegen aan aanvraag
					</Link>
				</div>
			</Reveal>

			<Footer />
		</>
	);
}

export default ProductenDetail;
